// Built-in sample data so the UI runs with no backend (REACT_APP_USE_MOCK=true).
// It returns exactly the same shapes as the Spring Boot API, so switching to the
// real API later changes nothing in the pages. Two fictional clients, like the
// SampleDataLoader on the server.

import { getSelectedClientId } from "../services/clientSelection";
import * as assess from "./mockAssessment";
import { DEPARTMENTS } from "./departments";
import {
  TIER, WORKFLOW, CONTROL, EVIDENCE, RISK_LEVEL, RISK_STATUS, POLICY, AUDIT, FINDING_STATUS,
} from "../constants";

const notFound = () => Promise.reject({ response: { status: 404, data: { message: "Application not found" } }, message: "Application not found" });
const delay = (value) => new Promise((resolve) => setTimeout(() => resolve(value), 120));

const TODAY = new Date();
const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (n) => {
  const d = new Date(TODAY);
  d.setDate(d.getDate() + n);
  return iso(d);
};

function rng(seed) {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
}

const CLIENTS = DEPARTMENTS.map((d) => ({ id: d.id, name: d.name, industry: "Department" }));

const OWNERS = ["Priya Raman", "Marcus Webb", "Elena Ortiz", "Daniel Kim", "Aisha Bello", "Tom Nguyen"];

const A = "AWAITING_RISK_ASSESSMENT";

const SOC2 = ["SOC2", "SOC 2 Trust Services Criteria", [
  ["CC1.1", "Integrity and ethical values"], ["CC2.1", "Quality information is used"],
  ["CC3.2", "Risks are identified and analyzed"], ["CC5.2", "Technology general controls"],
  ["CC6.1", "Logical access controls"], ["CC6.6", "Boundary protection"],
  ["CC7.2", "Security event monitoring"], ["CC8.1", "Change management"]]];
const AIRMF = ["NISTAIRMF", "NIST AI Risk Management Framework", [
  ["GOVERN 1.1", "Legal and regulatory requirements understood"], ["GOVERN 1.6", "AI system inventory maintained"],
  ["MAP 1.1", "Intended purpose and context documented"], ["MAP 3.1", "Benefits and costs examined"],
  ["MEASURE 2.5", "Validity and reliability demonstrated"], ["MEASURE 2.11", "Fairness and bias evaluated"],
  ["MANAGE 1.1", "Risk responses determined"], ["MANAGE 4.1", "Post-deployment monitoring in place"]]];
const HIPAA = ["HIPAA", "HIPAA Security Rule", [
  ["164.308(a)(1)", "Security risk analysis"], ["164.308(a)(3)", "Workforce security"],
  ["164.308(a)(5)", "Security awareness training"], ["164.310(d)", "Device and media controls"],
  ["164.312(a)(1)", "Access control"], ["164.312(b)", "Audit controls"], ["164.312(e)(1)", "Transmission security"]]];
const GLBA = ["GLBA", "GLBA Safeguards Rule", [
  ["314.4(b)", "Written risk assessment"], ["314.4(c)(1)", "Access controls"], ["314.4(c)(3)", "Data encryption"],
  ["314.4(d)", "Testing and monitoring"], ["314.4(f)", "Service provider oversight"]]];

const RISKS = [
  ["Model outputs expose sensitive personal data", "Data privacy", 4, 5, "MITIGATING", "Mitigate"],
  ["Biased outcomes in automated decisions", "Fairness", 3, 5, "OPEN", "Mitigate"],
  ["Hallucinated content reaches customers", "Reliability", 4, 4, "MITIGATING", "Mitigate"],
  ["Third-party AI vendor suffers a breach", "Third party", 3, 5, "OPEN", "Transfer"],
  ["Staff use unapproved AI tools (shadow AI)", "Governance", 5, 3, "OPEN", "Mitigate"],
  ["Model drift degrades accuracy over time", "Reliability", 4, 3, "MITIGATING", "Mitigate"],
  ["Insufficient audit logging for AI decisions", "Security", 3, 3, "OPEN", "Mitigate"],
  ["Prompt injection exposes internal data", "Security", 3, 4, "OPEN", "Mitigate"],
  ["Evidence collection is manual and often late", "Compliance", 4, 2, "ACCEPTED", "Accept"],
  ["New AI regulation changes obligations", "Regulatory", 3, 4, "OPEN", "Mitigate"],
  ["No named owner for several models", "Governance", 3, 2, "CLOSED", "Mitigate"],
  ["Training data licensing or consent gaps", "Legal", 2, 4, "OPEN", "Avoid"],
];

const POLICIES = [
  ["AI Acceptable Use Policy", "2.1", "APPROVED", 30], ["Information Security Policy", "4.0", "APPROVED", 120],
  ["Data Classification and Handling", "3.2", "APPROVED", -15], ["Third-Party Risk Management", "1.4", "IN_REVIEW", 45],
  ["Incident Response Plan", "5.0", "APPROVED", 200], ["Access Control Policy", "2.3", "APPROVED", -40],
  ["Model Risk Management Standard", "0.9", "DRAFT", 60], ["Business Continuity Plan", "3.0", "APPROVED", 90],
];

const FINDINGS = [
  [0, "Quarterly access reviews not completed on time", "MEDIUM", "CLOSED"],
  [0, "Change tickets missing approval evidence", "LOW", "CLOSED"],
  [1, "Encryption not enforced on legacy file share", "HIGH", "IN_PROGRESS"],
  [1, "Workforce training records incomplete", "MEDIUM", "OPEN"],
  [1, "Audit logs retained for under six years", "MEDIUM", "OPEN"],
  [1, "Vendor BAA missing for one analytics provider", "CRITICAL", "OPEN"],
  [2, "No central inventory of AI models", "HIGH", "IN_PROGRESS"],
  [2, "Bias testing not documented for decisioning models", "HIGH", "OPEN"],
];


// Sample questionnaires per department: [application, status, risky-answer weight threshold, answers given]
const SEED_ASSESSMENTS = {
  Finance: [["Vendor Contract Reviewer", "SUBMITTED", 4], ["Financial Close Copilot", "DRAFT", 4, 8]],
  HR: [["Performance Review Summarizer", "SUBMITTED", 3], ["Resume Screening Tool", "DRAFT", 4, 9]],
  Clinical: [["Discharge Summary Generator", "SUBMITTED", 4]],
  Legal: [["Outside Counsel Invoice Review", "DRAFT", 4, 7]],
  Technology: [["Test Case Generator", "DRAFT", 5, 6]],
  Privacy: [["Privacy Impact Assessment Drafter", "SUBMITTED", 5]],
};

const SEVERITY_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];
const EVIDENCE_TYPES = ["Report", "Screenshot", "Log export", "Policy document", "Ticket export"];

function controlStatus(random) {
  const roll = random() * 100;
  if (roll < 55) return "IMPLEMENTED";
  if (roll < 80) return "PARTIALLY_IMPLEMENTED";
  if (roll < 92) return "NOT_IMPLEMENTED";
  return "NOT_APPLICABLE";
}

function evidenceStatus(e) {
  if (!e.collectedOn) return "MISSING";
  if (e.expiresOn) {
    if (e.expiresOn < addDays(0)) return "EXPIRED";
    if (e.expiresOn <= addDays(30)) return "EXPIRING_SOON";
  }
  return "CURRENT";
}

function riskLevel(score) {
  if (score >= 20) return "CRITICAL";
  if (score >= 12) return "HIGH";
  if (score >= 6) return "MEDIUM";
  return "LOW";
}

function buildStore(clientId) {
  const random = rng(clientId * 13 + 7);
  const dept = DEPARTMENTS.find((d) => d.id === clientId) || DEPARTMENTS[0];
  let nextId = 1;
  const id = () => nextId++;

  const apps = dept.apps.map((s) => {
    const assessed = s[9] !== A;
    const daysAgo = 10 + Math.floor(random() * 250);
    const last = assessed ? addDays(-daysAgo) : null;
    return {
      id: id(), name: s[0], description: s[1], vendor: s[2], owner: s[3], businessUnit: s[4],
      useCase: s[5], modelType: s[6], deployment: s[7], dataSensitivity: s[8],
      riskTier: s[10], status: s[9], lastAssessedOn: last,
      nextReviewDue: last ? addDays(365 - daysAgo) : null,
    };
  });

  const byCode = { SOC2, HIPAA, GLBA, NISTAIRMF: AIRMF };
  const frameworkDefs = dept.frameworks.map((code) => byCode[code]);
  const frameworks = [];
  const controls = [];
  const evidence = [];
  frameworkDefs.forEach(([code, name, list]) => {
    const fid = id();
    frameworks.push({ id: fid, code, name });
    list.forEach(([ref, title]) => {
      const owner = OWNERS[Math.floor(random() * OWNERS.length)];
      const status = controlStatus(random);
      const cid = id();
      controls.push({
        id: cid, frameworkId: fid, frameworkCode: code, controlRef: ref, title, description: "",
        owner, status, lastReviewed: addDays(-(5 + Math.floor(random() * 120))),
      });
      if (status === "NOT_APPLICABLE") return;
      const implemented = status === "IMPLEMENTED";
      const partial = status === "PARTIALLY_IMPLEMENTED";
      if (!implemented && !partial && random() < 0.5) return;
      const e = {
        id: id(), controlId: cid, controlRef: ref, frameworkCode: code,
        title: `${title} – ${implemented ? "evidence" : "evidence request"}`,
        type: EVIDENCE_TYPES[Math.floor(random() * EVIDENCE_TYPES.length)], owner,
        collectedOn: null, expiresOn: null,
      };
      if (implemented || (partial && random() < 0.5)) {
        e.collectedOn = addDays(-(20 + Math.floor(random() * 280)));
        e.expiresOn = addDays(Math.floor(random() * 350) - 25);
      }
      evidence.push(e);
    });
  });

  const risks = RISKS.map((r, i) => {
    const app = i % 3 !== 2 ? apps[i % apps.length] : null;
    return {
      id: id(), title: r[0], description: "", category: r[1], likelihood: r[2], impact: r[3],
      status: r[4], treatment: r[5], owner: OWNERS[Math.floor(random() * OWNERS.length)],
      applicationId: app ? app.id : null, applicationName: app ? app.name : null,
      dueDate: addDays(Math.floor(random() * 150) - 20),
    };
  });

  const policies = POLICIES.map((p) => ({
    id: id(), title: p[0], version: p[1], status: p[2],
    owner: OWNERS[Math.floor(random() * OWNERS.length)],
    nextReviewDue: addDays(p[3]), lastReviewed: addDays(p[3] - 365),
  }));

  const auditFrameworks = [dept.frameworks[0], dept.frameworks[1], "NISTAIRMF"];
  const auditStatuses = ["COMPLETED", "IN_PROGRESS", "PLANNED"];
  const audits = [
    `${auditFrameworks[0]} annual audit`,
    `${auditFrameworks[1]} assessment`,
    "AI governance readiness review",
  ].map((name, i) => ({
    id: id(), name, frameworkCode: auditFrameworks[i],
    auditor: ["Harbor & Pike LLP", "Clearwater Assurance", "Internal Audit"][i],
    status: auditStatuses[i], startDate: addDays(-(240 - i * 150)), endDate: addDays(-(180 - i * 150)),
  }));

  const findings = FINDINGS.map((f) => ({
    id: id(), auditId: audits[f[0]].id, auditName: audits[f[0]].name, title: f[1],
    severity: f[2], status: f[3], owner: OWNERS[Math.floor(random() * OWNERS.length)],
    dueDate: addDays(Math.floor(random() * 120) - 25),
  }));

  // Vendors are the third parties behind this department's applications, plus one non-AI supplier.
  const vendorRows = [];
  apps.forEach((app) => {
    if (app.vendor === "In-house" || vendorRows.some((v) => v.name === app.vendor)) return;
    vendorRows.push({ name: app.vendor, service: app.useCase, riskTier: app.riskTier, hasAiComponents: true });
  });
  vendorRows.push({ name: "Cloudline Hosting", service: "Cloud infrastructure", riskTier: "TIER_1", hasAiComponents: false });
  const vendors = vendorRows.map((v) => ({
    id: id(), name: v.name, service: v.service, riskTier: v.riskTier, hasAiComponents: v.hasAiComponents,
    lastAssessedOn: v.riskTier ? addDays(-(30 + Math.floor(random() * 300))) : null,
    contractEnd: addDays(60 + Math.floor(random() * 500)),
  }));

  // Sample questionnaires: one waiting for analyst review, one half-answered, the rest not started.
  const assessments = {};
  const seed = (name, status, threshold, upTo) => {
    const app = apps.find((a) => a.name === name);
    if (!app) return;
    assessments[app.id] = {
      status, answers: assess.sampleAnswers(app, threshold, upTo), respondent: app.owner,
      submittedOn: status === "SUBMITTED" ? addDays(-2) : null,
    };
  };
  (SEED_ASSESSMENTS[dept.name] || []).forEach(([name, status, threshold, upTo]) => seed(name, status, threshold, upTo));

  return { nextId, apps, assessments, frameworks, controls, evidence, risks, policies, audits, findings, vendors };
}

const stores = {};
function store() {
  const clientId = Number(getSelectedClientId()) || 1;
  if (!stores[clientId]) stores[clientId] = buildStore(clientId);
  return { s: stores[clientId], clientId };
}

// ── DTO builders (same shapes as the Spring Boot API) ───────────────────────

const appDto = (a, s) => {
  const rec = s && s.assessments[a.id];
  const assessmentStatus = rec ? rec.status : "NOT_STARTED";
  const applicationType = assess.appType(a);
  return {
    ...a,
    applicationType,
    applicationTypeLabel: applicationType === "INTERNAL" ? "Internal build" : "Vendor-based",
    assessmentStatus,
    assessmentStatusLabel: assess.ASSESSMENT_STATUS[assessmentStatus],
    ...appLabels(a),
  };
};
const appLabels = (a) => ({
  riskTierLabel: a.riskTier ? TIER[a.riskTier].label : "Not yet assessed",
  statusLabel: WORKFLOW[a.status].label,
});

function frameworkDtos(s) {
  return s.frameworks.map((f) => {
    const list = s.controls.filter((c) => c.frameworkId === f.id);
    const count = (st) => list.filter((c) => c.status === st).length;
    const implemented = count("IMPLEMENTED");
    const partial = count("PARTIALLY_IMPLEMENTED");
    const notApplicable = count("NOT_APPLICABLE");
    const applicable = list.length - notApplicable;
    return {
      ...f, version: "", description: "", totalControls: list.length, implemented,
      partiallyImplemented: partial, notImplemented: count("NOT_IMPLEMENTED"), notApplicable,
      compliancePct: applicable ? Math.round(((implemented + 0.5 * partial) * 100) / applicable) : 0,
    };
  });
}

const evidenceDtos = (s) => s.evidence.map((e) => {
  const status = evidenceStatus(e);
  return { ...e, status, statusLabel: EVIDENCE[status].label };
});

const riskDtos = (s) => s.risks.map((r) => {
  const score = r.likelihood * r.impact;
  const level = riskLevel(score);
  return { ...r, score, level, levelLabel: RISK_LEVEL[level].label, statusLabel: RISK_STATUS[r.status].label };
}).sort((a, b) => b.score - a.score);

const policyDtos = (s) => s.policies.map((p) => ({
  ...p, statusLabel: POLICY[p.status].label,
  reviewOverdue: p.status !== "RETIRED" && p.nextReviewDue < addDays(0),
}));

const findingDtos = (s) => s.findings.map((f) => ({
  ...f, severityLabel: RISK_LEVEL[f.severity].label, statusLabel: FINDING_STATUS[f.status].label,
  overdue: f.status !== "CLOSED" && f.dueDate < addDays(0),
})).sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity));

const auditDtos = (s) => s.audits.map((a) => ({
  ...a, statusLabel: AUDIT[a.status].label,
  openFindings: s.findings.filter((f) => f.auditId === a.id && f.status !== "CLOSED").length,
}));

const vendorDtos = (s) => s.vendors.map((v) => ({
  ...v, riskTierLabel: v.riskTier ? TIER[v.riskTier].label : "Not yet assessed",
}));

function slices(map, keys, items, classify) {
  return keys.map((key) => ({
    key, label: map[key].label, count: items.filter((i) => classify(i) === key).length,
  }));
}

function dashboard(s, clientId) {
  const client = CLIENTS.find((c) => c.id === clientId);
  const apps = s.apps;
  const tierKeys = ["TIER_1", "TIER_2", "TIER_3", "TIER_4"];
  const riskTierBreakdown = [
    ...slices(TIER, tierKeys, apps, (a) => a.riskTier),
    { key: "UNASSIGNED", label: TIER.UNASSIGNED.label, count: apps.filter((a) => !a.riskTier).length },
  ];
  const workflowBreakdown = slices(WORKFLOW, Object.keys(WORKFLOW), apps, (a) => a.status);

  const frameworks = frameworkDtos(s);
  const applicable = s.controls.filter((c) => c.status !== "NOT_APPLICABLE");
  const impl = s.controls.filter((c) => c.status === "IMPLEMENTED").length;
  const part = s.controls.filter((c) => c.status === "PARTIALLY_IMPLEMENTED").length;

  const evs = evidenceDtos(s);
  const openRisks = riskDtos(s).filter((r) => r.status !== "CLOSED");
  const openFindings = findingDtos(s).filter((f) => f.status !== "CLOSED");
  const riskLevelBreakdown = slices(RISK_LEVEL, Object.keys(RISK_LEVEL), openRisks, (r) => r.level);
  const vendorTierBreakdown = [
    ...slices(TIER, tierKeys, s.vendors, (v) => v.riskTier),
    { key: "UNASSIGNED", label: TIER.UNASSIGNED.label, count: s.vendors.filter((v) => !v.riskTier).length },
  ];
  const n = (st) => apps.filter((a) => a.status === st).length;

  return {
    clientName: client ? client.name : "Client",
    kpis: {
      totalApplications: apps.length,
      awaitingRiskAssessment: n("AWAITING_RISK_ASSESSMENT"),
      awaitingApproval: n("AWAITING_APPROVAL"),
      approvedApplications: n("APPROVED"),
      highAndCriticalRisks: openRisks.filter((r) => r.level === "CRITICAL" || r.level === "HIGH").length,
      controlCompliancePct: applicable.length ? Math.round(((impl + 0.5 * part) * 100) / applicable.length) : 0,
      evidenceNeedingAttention: evs.filter((e) => e.status !== "CURRENT").length,
      openFindings: openFindings.length,
      highRiskVendors: s.vendors.filter((v) => v.riskTier === "TIER_1" || v.riskTier === "TIER_2").length,
      policiesOverdueReview: policyDtos(s).filter((p) => p.reviewOverdue).length,
    },
    riskTierBreakdown,
    workflowBreakdown,
    frameworkCompliance: frameworks.map((f) => ({
      code: f.code, name: f.name, compliancePct: f.compliancePct, totalControls: f.totalControls,
    })),
    controlBreakdown: slices(CONTROL, Object.keys(CONTROL), s.controls, (c) => c.status),
    evidenceBreakdown: slices(EVIDENCE, Object.keys(EVIDENCE), evs, (e) => e.status),
    riskLevelBreakdown,
    findingSeverityBreakdown: slices(RISK_LEVEL, Object.keys(RISK_LEVEL), openFindings, (f) => f.severity),
    vendorTierBreakdown,
    topRisks: openRisks.slice(0, 5).map((r) => ({
      id: r.id, title: r.title, score: r.score, levelLabel: r.levelLabel, owner: r.owner, statusLabel: r.statusLabel,
    })),
    applicationsNeedingAttention: apps
      .filter((a) => a.status === "AWAITING_RISK_ASSESSMENT" || a.status === "AWAITING_APPROVAL")
      .slice(0, 6)
      .map((a) => ({
        id: a.id, name: a.name, owner: a.owner, statusLabel: WORKFLOW[a.status].label,
        riskTierLabel: a.riskTier ? TIER[a.riskTier].label : "Not yet assessed",
      })),
  };
}

// ── Public mock API (mirrors the real endpoints) ────────────────────────────

// People in the sample-data user directory (the Users page). Same shape as the API.
const STAFF_ROLES = ["PlatformAdmin", "GRCManager", "RiskAnalyst"];
let mockUsers = [
  { username: "u-1", email: "demo@verainsight.com", name: "Demo Admin", status: "CONFIRMED", enabled: true, role: "PlatformAdmin", departmentId: null, self: true, createdAt: "2026-10-01T10:00:00Z" },
  { username: "u-2", email: "priya.raman@example.com", name: "Priya Raman", status: "CONFIRMED", enabled: true, role: "ControlOwner", departmentId: 1, self: false, createdAt: "2026-10-12T10:00:00Z" },
  { username: "u-3", email: "sandeep.sinha@itnmn.com", name: "Sandeep Sinha", status: "CONFIRMED", enabled: true, role: null, departmentId: null, self: false, createdAt: "2026-11-04T16:30:00Z" },
  { username: "u-4", email: "new.hire@example.com", name: "New Hire", status: "CONFIRMED", enabled: true, role: null, departmentId: null, self: false, createdAt: "2026-11-05T09:10:00Z" },
];
const userDtos = () =>
  mockUsers
    .map((u) => ({
      ...u, pending: !u.role,
      department: u.departmentId ? (CLIENTS.find((c) => c.id === u.departmentId) || {}).name : null,
    }))
    .sort((a, b) => (b.pending - a.pending) || String(b.createdAt).localeCompare(String(a.createdAt)));
const mockFail = (status, message) =>
  Promise.reject({ response: { status, data: { message } }, message });

export const mockApi = {
  getUsers: () => delay(userDtos()),
  setUserAccess: (username, { role, departmentId }) => {
    const u = mockUsers.find((x) => x.username === username);
    if (!u) return mockFail(404, "That user no longer exists. Refresh the list.");
    if (u.self) return mockFail(400, "You can't change your own access. Ask another platform admin to do it.");
    if (STAFF_ROLES.includes(role) && departmentId) return mockFail(400, `${role} sees every department, so it takes no department.`);
    if (!STAFF_ROLES.includes(role) && !departmentId) return mockFail(400, `Choose a department for the ${role} role.`);
    u.role = role;
    u.departmentId = STAFF_ROLES.includes(role) ? null : Number(departmentId);
    return delay(null);
  },
  removeUser: (username) => {
    const u = mockUsers.find((x) => x.username === username);
    if (u && u.self) return mockFail(400, "You can't remove your own account. Ask another platform admin to do it.");
    mockUsers = mockUsers.filter((x) => x.username !== username);
    return delay(null);
  },
  getMe: () => {
    const { clientId } = store();
    const client = CLIENTS.find((c) => c.id === clientId) || CLIENTS[0];
    return delay({
      email: "demo@verainsight.com", roles: ["PlatformAdmin"], staff: true, canWrite: true,
      canApprove: true, securityEnabled: false, client, availableClients: CLIENTS,
    });
  },
  getDashboard: () => { const { s, clientId } = store(); return delay(dashboard(s, clientId)); },
  getApplications: () => {
    const { s } = store();
    return delay(s.apps.map((a) => appDto(a, s)).sort((a, b) => a.name.localeCompare(b.name)));
  },
  createApplication: (request) => {
    const { s } = store();
    const type = request.applicationType || (request.vendor && request.vendor !== "Internal" ? "VENDOR" : "INTERNAL");
    const app = {
      id: s.nextId++, description: "", vendor: "", owner: "", businessUnit: "", useCase: "",
      modelType: "", dataSensitivity: "", ...request,
      deployment: type === "INTERNAL" ? "Internal build" : "Third-party SaaS",
      riskTier: null, status: "AWAITING_RISK_ASSESSMENT", lastAssessedOn: null, nextReviewDue: null,
    };
    delete app.applicationType;
    s.apps.push(app);
    return delay(appDto(app, s));
  },
  updateApplicationStatus: (appId, { status, riskTier }) => {
    const { s } = store();
    const app = s.apps.find((a) => a.id === appId);
    if (riskTier) app.riskTier = riskTier;
    if (status === "AWAITING_RISK_ASSESSMENT") app.riskTier = null;
    if (status === "RISK_ASSESSMENT_COMPLETED") {
      app.lastAssessedOn = addDays(0);
      app.nextReviewDue = addDays(365);
    }
    app.status = status;
    return delay(appDto(app, s));
  },
  getAssessment: (appId) => {
    const { s } = store();
    const app = s.apps.find((a) => a.id === Number(appId));
    if (!app) return notFound();
    return delay(assess.assessmentDto(app, s.assessments[app.id]));
  },
  saveAssessment: (appId, body) => {
    const { s } = store();
    const app = s.apps.find((a) => a.id === Number(appId));
    if (!app) return notFound();
    const out = assess.saveDraft(app, s.assessments[app.id], body, addDays(0));
    if (out.error) return assess.fail(out.error);
    s.assessments[app.id] = out.rec;
    return delay(assess.assessmentDto(app, out.rec));
  },
  submitAssessment: (appId) => {
    const { s } = store();
    const app = s.apps.find((a) => a.id === Number(appId));
    if (!app) return notFound();
    const out = assess.submit(app, s.assessments[app.id], addDays(0));
    if (out.error) return assess.fail(out.error);
    return delay(assess.assessmentDto(app, out.rec));
  },
  reviewAssessment: (appId, body) => {
    const { s } = store();
    const app = s.apps.find((a) => a.id === Number(appId));
    if (!app) return notFound();
    const out = assess.review(app, s.assessments[app.id], body, addDays(0));
    if (out.error) return assess.fail(out.error);
    if (out.completed) app.nextReviewDue = addDays(365);
    return delay(assess.assessmentDto(app, out.rec));
  },
  getFrameworks: () => delay(frameworkDtos(store().s)),
  getControls: (frameworkId) => {
    const { s } = store();
    const counts = {};
    s.evidence.forEach((e) => { counts[e.controlId] = (counts[e.controlId] || 0) + 1; });
    return delay(
      s.controls
        .filter((c) => !frameworkId || c.frameworkId === Number(frameworkId))
        .map((c) => ({ ...c, statusLabel: CONTROL[c.status].label, evidenceCount: counts[c.id] || 0 }))
    );
  },
  getEvidence: () => {
    const order = { MISSING: 0, EXPIRED: 1, EXPIRING_SOON: 2, CURRENT: 3 };
    return delay(evidenceDtos(store().s).sort((a, b) => order[a.status] - order[b.status] || a.title.localeCompare(b.title)));
  },
  getRisks: () => delay(riskDtos(store().s)),
  getPolicies: () => delay(policyDtos(store().s)),
  getAudits: () => delay(auditDtos(store().s)),
  getFindings: () => delay(findingDtos(store().s)),
  getVendors: () => delay(vendorDtos(store().s)),
};
